import { FC, useMemo, useState } from "react";
import { MDBCol, MDBRow, MDBSwitch } from "mdb-react-ui-kit";
import { ETypePackage, ModalSize } from "@/models/common/enums";
import TagsPackageType from "@/components/detail/tags/TagsPackageType";
import {
    useIsLG,
    useIsMD,
    useIsXL,
    useWindowSizes,
} from "@/utils/hooks/useWindowSizes";
import { formatPrice } from "@/utils/common/formatPrice";
import { useCurrency } from "@/utils/hooks/useCurrency";
import travelExperienceStyles from "@/styles/detail/DetailBookingSidebar/TravelExperienceSectionDetail.module.scss";
import generalSvg from "@/utils/svg-icon/general";
import socialAndBrandSvg from "@/utils/svg-icon/socialAndBrand";
import InfoModalContainer from "../common/info/InfoModal";
import { TourReadableTO } from "../../models/detail";
import packageDetailCustomSvg from "../../utils/svg-icon/detailPackageCustom";
import styles from "../../styles/detail/DetailHead.module.scss";

const HeadBox: FC<{
    packageDetail: TourReadableTO;
    days: string;
    isMobile?: boolean;
    isPremium: boolean;
    setIsPremium: (el: boolean) => void;
    typePackage: ETypePackage;
    className?: string;
    showAccomodations?: boolean;
}> = ({
    packageDetail: initialPackageDetail,
    days,
    isMobile,
    isPremium,
    setIsPremium,
    typePackage,
    className = "",
    showAccomodations = true,
}) => {
    const { Ticket, GeoTag, Keys } = packageDetailCustomSvg;

    const isMD = useIsMD();
    const isLG = useIsLG();
    const isXL = useIsXL();
    const [windowWidth] = useWindowSizes();
    const isLargeScreen = windowWidth >= 1920;

    const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
    const { Star } = generalSvg;
    const { SketchLine } = socialAndBrandSvg;

    // Il backend restituisce già i prezzi convertiti in base all'header X-Currency
    // Non serve conversione client-side perché quando cambia valuta la pagina si ricarica
    // e la queryKey include currency per invalidare la cache
    const packageDetail = initialPackageDetail;
    const { currency } = useCurrency();

    const paddingTop = useMemo(() => {
        if (!isMobile && (isMD || isLG || isXL)) return 1;
        if (isMobile) return 3;
        return 1;
    }, [isLG, isMD, isMobile, isXL]);

    // Ordina le destinazioni in base all'ordine degli itinerari
    const orderedDestinations = useMemo(() => {
        // Ordina gli itinerari per giorno
        const sortedItineraries = [...packageDetail.itineraries].sort(
            (first, second) => +first.day - +second.day
        );

        // Estrae tutte le città dagli itinerari ordinati
        const cities: string[] = [];
        const seenCities = new Set<string>();

        sortedItineraries.forEach(itinerary => {
            itinerary.itinerary.destinations.forEach(destination => {
                if (!seenCities.has(destination.city)) {
                    cities.push(destination.city);
                    seenCities.add(destination.city);
                }
            });
        });

        return cities;
    }, [packageDetail.itineraries]);

    return (
        <>
            <div
                className={`${styles.headLowerContainer} mt-4  d-flex ${className}justify-content-center`}
            >
                <div
                    className={
                        isMobile
                            ? styles.headLowerContainerMobile
                            : styles.headLowerContentContainer
                    }
                >
                    <MDBRow center>
                        <MDBCol className={styles.headRowCardContainer}>
                            <MDBRow
                                className={`${styles.headCardContainer} flex-column-reverse flex-lg-row`}
                                style={{ paddingTop: `${paddingTop}rem` }}
                            >
                                <MDBCol
                                    size={12}
                                    lg={9}
                                    className={`d-flex flex-column flex-md-row mt-3 mt-lg-0 ${styles.headCardColumnsContainer}`}
                                >
                                    <MDBCol
                                        size={12}
                                        md={4}
                                        className="d-flex justify-content-start justify-content-lg-center my-2 my-lg-0"
                                        style={
                                            isLargeScreen
                                                ? {
                                                      paddingLeft: 0,
                                                      paddingRight: 0,
                                                      marginLeft: 0,
                                                      marginRight: 0,
                                                  }
                                                : undefined
                                        }
                                    >
                                        <div className="d-flex align-items-center align-items-lg-start pt-md-3 w-100">
                                            <div className={styles.headIcon}>
                                                <Ticket alt="ticket" />
                                            </div>
                                            <div>
                                                <h4>Your Journey</h4>
                                                <p
                                                    className="pe-lg-4 m-0 mb-lg-3"
                                                    style={
                                                        isLargeScreen
                                                            ? {
                                                                  paddingRight: 0,
                                                              }
                                                            : undefined
                                                    }
                                                >
                                                    {`${days} days, ${
                                                        +days - 1
                                                    } nights`}
                                                </p>
                                            </div>
                                        </div>
                                    </MDBCol>
                                    <MDBCol
                                        size={12}
                                        md={4}
                                        className="d-flex justify-content-start justify-content-lg-center my-2 my-lg-0"
                                        style={
                                            isLargeScreen
                                                ? {
                                                      paddingLeft: 0,
                                                      paddingRight: 0,
                                                      marginLeft: -24,
                                                      marginRight: 0,
                                                  }
                                                : undefined
                                        }
                                    >
                                        <div className="d-flex align-items-center align-items-lg-start pt-md-3 w-100">
                                            <div
                                                className={
                                                    styles.headIconSecond
                                                }
                                            >
                                                <GeoTag alt="geotag" />
                                            </div>
                                            <div>
                                                <h4>Destinations</h4>
                                                <p
                                                    className="pe-lg-4 m-0 mb-lg-3"
                                                    style={
                                                        isLargeScreen
                                                            ? {
                                                                  paddingRight: 0,
                                                              }
                                                            : undefined
                                                    }
                                                >
                                                    {orderedDestinations.map(
                                                        (city, i) => (
                                                            <span
                                                                // eslint-disable-next-line react/no-array-index-key
                                                                key={`${city}_${i}`}
                                                            >
                                                                {(i
                                                                    ? ", "
                                                                    : "") +
                                                                    city}
                                                            </span>
                                                        )
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </MDBCol>
                                    {showAccomodations && (
                                        <MDBCol
                                            size={12}
                                            md={4}
                                            className="d-flex justify-content-start justify-content-lg-center my-2 my-lg-0"
                                            style={
                                                isLargeScreen
                                                    ? {
                                                          paddingLeft: 0,
                                                          paddingRight: 0,
                                                          marginLeft: -24,
                                                          marginRight: 0,
                                                      }
                                                    : undefined
                                            }
                                        >
                                            <div className="d-flex align-items-center align-items-lg-start pt-md-3 w-100">
                                                <div
                                                    className={
                                                        styles.headIconSecond
                                                    }
                                                >
                                                    <Keys alt="keys" />
                                                </div>
                                                <div>
                                                    <h4>Accommodations</h4>
                                                    <p
                                                        className="pe-lg-4 m-0 mb-lg-3"
                                                        style={
                                                            isLargeScreen
                                                                ? {
                                                                      paddingRight: 0,
                                                                  }
                                                                : undefined
                                                        }
                                                    >
                                                        {`${
                                                            isPremium
                                                                ? "Exclusive accommodation"
                                                                : "4* Hotels"
                                                        }`}
                                                    </p>
                                                </div>
                                            </div>
                                        </MDBCol>
                                    )}
                                </MDBCol>

                                <MDBCol
                                    size={12}
                                    md={3}
                                    className="d-flex justify-content-center mx-auto mx-lg-0"
                                >
                                    <div
                                        className={
                                            styles.priceAndLevelContainer
                                        }
                                    >
                                        <div
                                            className={
                                                styles.priceContentWrapper
                                            }
                                        >
                                            <div
                                                className={
                                                    styles.priceContainerInner
                                                }
                                            >
                                                <div
                                                    className={
                                                        styles.priceRowContainer
                                                    }
                                                >
                                                    <span
                                                        className={
                                                            styles.fromText
                                                        }
                                                    >
                                                        from
                                                    </span>
                                                    <div
                                                        className={
                                                            styles.newPriceContainer
                                                        }
                                                    >
                                                        <span
                                                            className={
                                                                styles.priceAmount
                                                            }
                                                        >
                                                            {formatPrice(
                                                                Number(
                                                                    packageDetail.startingPrice ||
                                                                        packageDetail.price ||
                                                                        0
                                                                ),
                                                                currency
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div
                                                    className={
                                                        styles.startingPriceTextContainer
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.startingPriceStyle
                                                        }
                                                    >
                                                        Starting price per
                                                        person in double room
                                                    </div>
                                                </div>
                                            </div>
                                            {typePackage ===
                                                ETypePackage.QUALITY_AND_PREMIUM && (
                                                <div
                                                    className={
                                                        styles.qualityPremiumToggle
                                                    }
                                                >
                                                    <span
                                                        className={`${
                                                            styles.qualityText
                                                        } ${
                                                            !isPremium
                                                                ? styles.qualityTextActive
                                                                : ""
                                                        }`}
                                                    >
                                                        Quality
                                                    </span>
                                                    <MDBSwitch
                                                        checked={isPremium}
                                                        onChange={() => {
                                                            setIsPremium(
                                                                !isPremium
                                                            );
                                                        }}
                                                        className={
                                                            styles.customSwitch
                                                        }
                                                    />
                                                    <span
                                                        className={`${
                                                            styles.premiumText
                                                        } ${
                                                            isPremium
                                                                ? styles.premiumTextActive
                                                                : ""
                                                        }`}
                                                    >
                                                        Premium
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        {typePackage !==
                                            ETypePackage.QUALITY_AND_PREMIUM && (
                                            <TagsPackageType
                                                isPremium={
                                                    typePackage ===
                                                    ETypePackage.ONLY_PREMIUM
                                                }
                                            />
                                        )}
                                    </div>
                                </MDBCol>
                            </MDBRow>
                        </MDBCol>
                    </MDBRow>
                </div>
            </div>
            <InfoModalContainer
                title=""
                show={showInfoModal}
                setShow={setShowInfoModal}
                size={ModalSize.LG}
            >
                <div className={travelExperienceStyles.container}>
                    <div
                        className={
                            travelExperienceStyles.containerPackageSection
                        }
                    >
                        <div
                            className={`${travelExperienceStyles.package} ${travelExperienceStyles.packageQuality}`}
                        >
                            <h1 className={travelExperienceStyles.packageTitle}>
                                Quality
                            </h1>
                            <p
                                className={
                                    travelExperienceStyles.packageDescription
                                }
                            >
                                Our quality package formula offers the best
                                travel experience, combining comfortable hotels
                                with fun group outings. Our tours are guided by
                                Italian experts—locals with a true passion for
                                tourism. Their goal is to make your journey fun,
                                insightful, relaxing, and truly unforgettable.
                            </p>
                            <div className={travelExperienceStyles.packageList}>
                                <div
                                    className={
                                        travelExperienceStyles.packageListIconQuality
                                    }
                                >
                                    <Star />
                                </div>
                                <p
                                    className={
                                        travelExperienceStyles.packageListText
                                    }
                                >
                                    4-stars hotel
                                </p>
                            </div>
                            <div className={travelExperienceStyles.packageList}>
                                <div
                                    className={
                                        travelExperienceStyles.packageListIconQuality
                                    }
                                >
                                    <Star />
                                </div>
                                <p
                                    className={
                                        travelExperienceStyles.packageListText
                                    }
                                >
                                    Round-trip private transfers from your
                                    arrival point to accommodation, tailored to
                                    your itinerary
                                </p>
                            </div>
                            <div className={travelExperienceStyles.packageList}>
                                <div
                                    className={
                                        travelExperienceStyles.packageListIconQuality
                                    }
                                >
                                    <Star />
                                </div>
                                <p
                                    className={
                                        travelExperienceStyles.packageListText
                                    }
                                >
                                    Group experiences
                                </p>
                            </div>
                            <div className={travelExperienceStyles.packageList}>
                                <div
                                    className={
                                        travelExperienceStyles.packageListIconQuality
                                    }
                                >
                                    <Star />
                                </div>
                                <p
                                    className={
                                        travelExperienceStyles.packageListText
                                    }
                                >
                                    Assistance before, during, and after your
                                    stay
                                </p>
                            </div>
                            <div className={travelExperienceStyles.packageList}>
                                <div
                                    className={
                                        travelExperienceStyles.packageListIconQuality
                                    }
                                >
                                    <Star />
                                </div>
                                <p
                                    className={
                                        travelExperienceStyles.packageListText
                                    }
                                >
                                    Italian Experts
                                </p>
                            </div>
                            <div className={travelExperienceStyles.packageList}>
                                <div
                                    className={
                                        travelExperienceStyles.packageListIconQuality
                                    }
                                >
                                    <Star />
                                </div>
                                <p
                                    className={
                                        travelExperienceStyles.packageListText
                                    }
                                >
                                    Optional: Flights and Travel Insurance
                                </p>
                            </div>
                        </div>
                        <div
                            className={`${travelExperienceStyles.package} ${travelExperienceStyles.packagePremium}`}
                        >
                            <h1 className={travelExperienceStyles.packageTitle}>
                                Premium
                            </h1>
                            <p
                                className={
                                    travelExperienceStyles.packageDescription
                                }
                            >
                                If you&apos;re looking to enjoy an exclusive
                                Italian experience, our premium package is
                                perfect for you. You&apos;ll stay in exclusive
                                and unique accommodations and enjoy the comfort
                                of private transfers. Your private party will be
                                guided by our Italian experts, ensuring an
                                unforgettable journey.
                            </p>
                            <div className={travelExperienceStyles.packageList}>
                                <div
                                    className={
                                        travelExperienceStyles.packageListIconPremium
                                    }
                                >
                                    <SketchLine />
                                </div>
                                <p
                                    className={
                                        travelExperienceStyles.packageListText
                                    }
                                >
                                    Exclusive and unique accommodations
                                </p>
                            </div>
                            <div className={travelExperienceStyles.packageList}>
                                <div
                                    className={
                                        travelExperienceStyles.packageListIconPremium
                                    }
                                >
                                    <SketchLine />
                                </div>
                                <p
                                    className={
                                        travelExperienceStyles.packageListText
                                    }
                                >
                                    Round-trip private transfers from your
                                    arrival point to accommodation, tailored to
                                    your itinerary
                                </p>
                            </div>
                            <div className={travelExperienceStyles.packageList}>
                                <div
                                    className={
                                        travelExperienceStyles.packageListIconPremium
                                    }
                                >
                                    <SketchLine />
                                </div>
                                <p
                                    className={
                                        travelExperienceStyles.packageListText
                                    }
                                >
                                    Private experiences
                                </p>
                            </div>
                            <div className={travelExperienceStyles.packageList}>
                                <div
                                    className={
                                        travelExperienceStyles.packageListIconPremium
                                    }
                                >
                                    <SketchLine />
                                </div>
                                <p
                                    className={
                                        travelExperienceStyles.packageListText
                                    }
                                >
                                    Assistance before, during, and after your
                                    stay
                                </p>
                            </div>
                            <div className={travelExperienceStyles.packageList}>
                                <div
                                    className={
                                        travelExperienceStyles.packageListIconPremium
                                    }
                                >
                                    <SketchLine />
                                </div>
                                <p
                                    className={
                                        travelExperienceStyles.packageListText
                                    }
                                >
                                    Italian Experts
                                </p>
                            </div>
                            <div className={travelExperienceStyles.packageList}>
                                <div
                                    className={
                                        travelExperienceStyles.packageListIconPremium
                                    }
                                >
                                    <SketchLine />
                                </div>
                                <p
                                    className={
                                        travelExperienceStyles.packageListText
                                    }
                                >
                                    Optional: Flights and Travel Insurance
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </InfoModalContainer>
        </>
    );
};

HeadBox.defaultProps = {
    isMobile: false,
};

export default HeadBox;
